import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DRAIN_TYPES, PnidAIData, PnidEquipment, PnidNode } from '../types';
import {
  formatInstrumentIdentity,
  getInstrumentPrimaryLabel,
  getInstrumentSecondaryLabel,
} from '../utils/instrumentIdentity';

export type RefType = 'equipment' | 'external' | 'node' | 'instrument' | 'drain' | 'manual';

export interface InstrumentRef {
  id: string;
  instrument_text?: string | null;
  generated_id?: string | null;
  instrument_type?: string | null;
}

export interface SourceDestSelectorProps {
  label: string;
  value: string;
  onChange: (value: string, refType: RefType, externalRef?: ExternalRefSelection) => void;
  aiData: PnidAIData | null;
  nodes: PnidNode[];
  equipments?: PnidEquipment[];
  instruments?: InstrumentRef[];
  includeDrain?: boolean;
  currentNodeId?: string;
  defaultTab?: RefType;
  forceExpanded?: boolean;
  autoFocusSearch?: boolean;
  onAdvance?: () => void;
  onHighlightEntity?: (id: string | null) => void;
  /** Read-only: render the current selection as a static value with no editing controls. */
  disabled?: boolean;
}

export type ExternalEndpoint = 'source' | 'destination';

export interface ExternalRefSelection {
  refText: string;
  pnid?: string | null;
  tag: string;
  context: string;
  lineNo?: string | null;
  endpoint?: ExternalEndpoint;
}

const isNodeRefPattern = (val: string): boolean => {
  const trimmed = val.trim().toLowerCase();
  return /^node\s*#?\s*\d+$/i.test(trimmed);
};

const INSTRUMENT_REF_PREFIX = 'Instrument: ';

export const isInstrumentRefPattern = (val: string): boolean =>
  val.trim().toLowerCase().startsWith(INSTRUMENT_REF_PREFIX.toLowerCase());

const instrumentDisplayLabel = (inst: InstrumentRef): string =>
  formatInstrumentIdentity(inst);

const buildInstrumentRef = (inst: InstrumentRef): string =>
  `${INSTRUMENT_REF_PREFIX}${instrumentDisplayLabel(inst)}`;

const getLegacyInstrumentRefLabel = (inst: InstrumentRef): string | null => {
  const manualTag = (inst.instrument_text || '').trim();
  const generatedId = (inst.generated_id || '').trim();
  if (!manualTag || !generatedId) return null;
  return `${manualTag} (${generatedId})`;
};

const instrumentMatchesRefLabel = (inst: InstrumentRef, label: string): boolean => {
  const normalizedLabel = label.trim();
  if (!normalizedLabel) return false;

  const primaryLabel = getInstrumentPrimaryLabel(inst);
  const generatedId = (inst.generated_id || '').trim();
  const manualTag = (inst.instrument_text || '').trim();
  const legacyLabel = getLegacyInstrumentRefLabel(inst);
  const legacySuffixMatch = normalizedLabel.match(/^(.*)\s+\(([^)]+)\)$/);
  const legacyPrimary = legacySuffixMatch?.[1]?.trim() || '';
  const legacyGenerated = legacySuffixMatch?.[2]?.trim() || '';

  return [
    instrumentDisplayLabel(inst),
    primaryLabel,
    generatedId,
    manualTag,
    legacyLabel,
    legacyPrimary,
    legacyGenerated,
  ].filter(Boolean).includes(normalizedLabel)
    || [primaryLabel, generatedId, manualTag].filter(Boolean).includes(legacyPrimary)
    || [primaryLabel, generatedId, manualTag].filter(Boolean).includes(legacyGenerated);
};

const parseInstrumentTag = (val: string): string => {
  const trimmed = val.trim();
  if (!trimmed.toLowerCase().startsWith(INSTRUMENT_REF_PREFIX.toLowerCase())) return '';
  return trimmed.slice(INSTRUMENT_REF_PREFIX.length).trim();
};

const inferTabFromValue = (value: string, includeDrain: boolean): RefType => {
  if (isInstrumentRefPattern(value)) return 'instrument';
  if (isNodeRefPattern(value)) return 'node';
  if (includeDrain && value && Object.prototype.hasOwnProperty.call(DRAIN_TYPES, value)) return 'drain';
  return 'equipment';
};

const inferExternalEndpoint = (label: string): ExternalEndpoint =>
  label.toLowerCase().includes('dest') ? 'destination' : 'source';

const directionMatchesEndpoint = (direction: string | null | undefined, endpoint: ExternalEndpoint): boolean => {
  const normalized = (direction || '').trim().toLowerCase();
  if (!normalized) return true;
  if (endpoint === 'source') return normalized === 'in' || normalized === 'source' || normalized === 'from';
  return normalized === 'out' || normalized === 'destination' || normalized === 'dest' || normalized === 'to';
};

const buildExternalContext = (
  ref: PnidAIData['external_equipment_refs'][number],
  endpoint: ExternalEndpoint,
  pnid: string | null,
  lineNo: string | null,
  refText: string
): string => {
  const localRef = ref.local_ref || (endpoint === 'source' ? ref.dest_ref : ref.source_ref) || null;
  const parts = [
    lineNo ? `PFD stream ${lineNo}` : null,
    pnid ? `DWG. ${pnid}` : null,
    localRef ? `${endpoint === 'source' ? 'to' : 'from'} local ${localRef}` : null,
    ref.service && ref.service !== refText ? ref.service : null,
  ].filter(Boolean);
  return parts.join(' · ') || ref.context || ref.reason || 'External Equipment';
};

const SourceDestSelector: React.FC<SourceDestSelectorProps> = ({
  label,
  value,
  onChange,
  aiData,
  nodes,
  equipments = [],
  instruments = [],
  includeDrain = false,
  currentNodeId,
  defaultTab,
  forceExpanded = false,
  autoFocusSearch = false,
  onAdvance,
  onHighlightEntity,
  disabled = false,
}) => {
  const [activeTab, setActiveTab] = useState<RefType>(() => defaultTab ?? inferTabFromValue(value, includeDrain));
  const [manualValue, setManualValue] = useState('');
  const [isExpanded, setIsExpanded] = useState(!value);
  const [searchQuery, setSearchQuery] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  const expanded = forceExpanded ? true : isExpanded;
  const externalEndpoint = useMemo(() => inferExternalEndpoint(label), [label]);

  // Resolve current value to an entity ID for canvas highlighting
  const resolvedEntityId = useMemo(() => {
    if (!value) return null;
    // Check if it's an instrument reference like "Instrument: FT-101"
    if (isInstrumentRefPattern(value)) {
      const tag = parseInstrumentTag(value);
      if (!tag) return null;
      const inst = (instruments || []).find((i) => instrumentMatchesRefLabel(i, tag));
      return inst?.id ?? null;
    }
    // Check if it's a node reference like "Node #5"
    const nodeMatch = value.match(/^Node\s*#?\s*(\d+)$/i);
    if (nodeMatch) {
      const nodeNo = parseInt(nodeMatch[1], 10);
      const node = (nodes || []).find((n) => n.node_no === nodeNo);
      return node?.id ?? null;
    }
    // Check if it matches an equipment tag
    const eq = equipments.find((e) => e.equipment_no === value);
    if (eq) return eq.id ?? null;
    return null;
  }, [value, nodes, equipments, instruments]);

  const handleHoverEnter = () => {
    if (resolvedEntityId) onHighlightEntity?.(resolvedEntityId);
  };
  const handleHoverLeave = () => {
    onHighlightEntity?.(null);
  };

  useEffect(() => {
    if (!defaultTab) return;
    setActiveTab(defaultTab);
  }, [defaultTab]);

  useEffect(() => {
    if (!autoFocusSearch) return;
    if (!expanded) return;
    if (!(activeTab === 'equipment' || activeTab === 'external' || activeTab === 'node' || activeTab === 'instrument')) return;
    const el = searchRef.current;
    if (!el) return;
    setTimeout(() => el.focus(), 0);
  }, [autoFocusSearch, expanded, activeTab]);

  const externalRefs = useMemo(() => {
    const refs: ExternalRefSelection[] = [];
    const seen = new Set<string>();
    const addRef = (ref: ExternalRefSelection) => {
      const refText = ref.refText.trim();
      if (!refText) return;
      const key = [
        ref.endpoint || '',
        refText.toLowerCase(),
        (ref.pnid || '').toLowerCase(),
        (ref.lineNo || '').toLowerCase(),
      ].join('|');
      if (seen.has(key)) return;
      seen.add(key);
      refs.push({
        ...ref,
        refText,
        tag: ref.tag || refText,
        context: ref.context || 'External Equipment',
      });
    };

    if (aiData?.external_equipment_refs) {
      for (const ref of aiData.external_equipment_refs) {
        if (ref.external_ref) {
          if (!directionMatchesEndpoint(ref.direction, externalEndpoint)) continue;
          const pnid = ref.external_pnid || ref.external_drawing || ref.pnid_name || null;
          const lineNo = ref.stream_no || ref.line_no || ref.tag || null;
          addRef({
            refText: ref.external_ref,
            pnid,
            tag: lineNo || ref.external_ref,
            context: buildExternalContext(ref, externalEndpoint, pnid, lineNo, ref.external_ref),
            lineNo,
            endpoint: externalEndpoint,
          });
          continue;
        }

        const refText = externalEndpoint === 'destination'
          ? ref.dest_ref || ref.tag
          : ref.source_ref || ref.tag;
        const pnid = externalEndpoint === 'destination'
          ? ref.dest_pnid || ref.pnid_name || null
          : ref.source_pnid || ref.pnid_name || null;
        addRef({
          refText,
          pnid,
          tag: ref.tag || refText,
          context: [
            pnid ? `DWG. ${pnid}` : null,
            ref.reason || ref.context || 'External Equipment',
          ].filter(Boolean).join(' · '),
          lineNo: ref.line_no || ref.stream_no || null,
          endpoint: externalEndpoint,
        });
      }
    }

    if (aiData?.tie_ins) {
      for (const tieIn of aiData.tie_ins) {
        if (!tieIn.from) continue;
        addRef({
          refText: tieIn.from,
          pnid: null,
          tag: tieIn.from,
          context: `Tie-in: ${tieIn.service || tieIn.direction}`,
          endpoint: externalEndpoint,
        });
      }
    }

    return refs;
  }, [aiData, externalEndpoint]);

  const availableNodes = useMemo(() => {
    return (nodes || []).filter((n) => n.id !== currentNodeId);
  }, [nodes, currentNodeId]);

  const allEquipment = useMemo(() => {
    const aiEquipment = aiData?.equipment_in_scope || [];
    const manualEquipment = equipments.map((eq) => ({
      tag: eq.equipment_no,
      name: eq.description,
      type: eq.equipment_type,
    }));

    const seen = new Set<string>();
    const result: { tag: string; name: string; type: string }[] = [];

    for (const eq of manualEquipment) {
      if (eq.tag && !seen.has(eq.tag)) {
        seen.add(eq.tag);
        result.push(eq);
      }
    }
    for (const eq of aiEquipment) {
      if (eq.tag && !seen.has(eq.tag)) {
        seen.add(eq.tag);
        result.push(eq);
      }
    }

    return result;
  }, [aiData?.equipment_in_scope, equipments]);

  const searchLower = searchQuery.toLowerCase();

  const filteredEquipment = useMemo(() => {
    if (!searchQuery) return allEquipment;
    return allEquipment.filter(
      (eq) =>
        eq.tag.toLowerCase().includes(searchLower) ||
        eq.name?.toLowerCase().includes(searchLower) ||
        eq.type?.toLowerCase().includes(searchLower)
    );
  }, [allEquipment, searchLower, searchQuery]);

  const filteredExternalRefs = useMemo(() => {
    if (!searchQuery) return externalRefs;
    return externalRefs.filter((ref) =>
      [ref.refText, ref.tag, ref.pnid || '', ref.context, ref.lineNo || '']
        .some((candidate) => candidate.toLowerCase().includes(searchLower))
    );
  }, [externalRefs, searchLower, searchQuery]);

  const filteredNodes = useMemo(() => {
    if (!searchQuery) return availableNodes;
    return availableNodes.filter(
      (node) =>
        `${node.node_no}`.includes(searchLower) ||
        node.line_no?.toLowerCase().includes(searchLower) ||
        node.source_ref?.toLowerCase().includes(searchLower) ||
        node.dest_ref?.toLowerCase().includes(searchLower)
    );
  }, [availableNodes, searchLower, searchQuery]);

  const selectableInstruments = useMemo(
    () => (instruments || []).filter((i) => i.instrument_text || i.generated_id),
    [instruments]
  );

  const filteredInstruments = useMemo(() => {
    if (!searchQuery) return selectableInstruments;
    return selectableInstruments.filter((inst) => {
      const label = instrumentDisplayLabel(inst).toLowerCase();
      const type = (inst.instrument_type || '').toLowerCase();
      return label.includes(searchLower) || type.includes(searchLower);
    });
  }, [selectableInstruments, searchLower, searchQuery]);

  const tabs: { id: RefType; label: string; show: boolean }[] = [
    { id: 'equipment', label: 'Equipment', show: true },
    { id: 'external', label: 'External', show: true },
    { id: 'node', label: 'Node/Line', show: availableNodes.length > 0 },
    { id: 'instrument', label: 'Instrument', show: selectableInstruments.length > 0 },
    { id: 'drain', label: 'Drain', show: includeDrain },
    { id: 'manual', label: 'Manual', show: true },
  ];

  const handleSelect = (val: string, type: RefType) => {
    onChange(val, type);
    if (!forceExpanded) setIsExpanded(false);
    setSearchQuery('');
    onAdvance?.();
  };

  const handleExternalSelect = (ref: ExternalRefSelection) => {
    onChange(ref.refText, 'external', ref);
    if (!forceExpanded) setIsExpanded(false);
    setSearchQuery('');
    onAdvance?.();
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter' && e.key !== 'Tab') return;
    if (e.key === 'Tab' && e.shiftKey) return;
    e.preventDefault();

    // If user hasn't typed anything, treat Enter/Tab as "confirm current value"
    // so the surrounding wizard can advance without changing the selection.
    if (!searchQuery.trim() && value.trim()) {
      onAdvance?.();
      return;
    }
    // No query and nothing selected yet: don't auto-pick a random first item.
    if (!searchQuery.trim() && !value.trim()) return;

    if (activeTab === 'equipment' && filteredEquipment.length) {
      handleSelect(filteredEquipment[0].tag, 'equipment');
      return;
    }
    if (activeTab === 'external' && filteredExternalRefs.length) {
      handleExternalSelect(filteredExternalRefs[0]);
      return;
    }
    if (activeTab === 'node' && filteredNodes.length) {
      handleSelect(`Node #${filteredNodes[0].node_no}`, 'node');
      return;
    }
    if (activeTab === 'instrument' && filteredInstruments.length) {
      handleSelect(buildInstrumentRef(filteredInstruments[0]), 'instrument');
      return;
    }
  };

  if (disabled) {
    return (
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 6 }}>{label}</div>
        <div
          onMouseEnter={handleHoverEnter}
          onMouseLeave={handleHoverLeave}
          style={{
            padding: '8px 12px',
            border: '1px solid #e5e7eb',
            borderRadius: 6,
            background: '#f9fafb',
            color: value ? '#111827' : '#9ca3af',
            fontSize: 12,
            cursor: 'not-allowed',
          }}
        >
          {value || 'None'}
        </div>
      </div>
    );
  }

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 6 }}>{label}</div>

      {!forceExpanded && !expanded && (
        <div
          onClick={() => setIsExpanded(true)}
          onMouseEnter={handleHoverEnter}
          onMouseLeave={handleHoverLeave}
          style={{
            padding: '8px 12px',
            border: '1px solid #e5e7eb',
            borderRadius: 6,
            background: 'white',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: 12,
          }}
        >
          <span style={{ color: value ? '#111827' : '#9ca3af' }}>{value || 'Click to select...'}</span>
          <span style={{ fontSize: 10, color: '#6b7280' }}>▼</span>
        </div>
      )}

      {!expanded ? null : (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: 8, background: 'white' }}>
          {!forceExpanded && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: 10, color: '#6b7280' }}>Select {label.toLowerCase()}</span>
              <button
                type="button"
                onClick={() => {
                  setIsExpanded(false);
                  setSearchQuery('');
                }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 10, color: '#6b7280' }}
              >
                ▲ Collapse
              </button>
            </div>
          )}

          {value && (
            <div
              onMouseEnter={handleHoverEnter}
              onMouseLeave={handleHoverLeave}
              style={{
                fontSize: 11,
                padding: '4px 8px',
                background: '#dbeafe',
                borderRadius: 4,
                marginBottom: 8,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: resolvedEntityId ? 'pointer' : undefined,
              }}
            >
              <span>
                <strong>Selected:</strong> {value}
              </span>
              <button
                type="button"
                onClick={() => onChange('', 'manual')}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#6b7280',
                  fontSize: 14,
                  padding: '0 4px',
                }}
                aria-label="Clear"
                title="Clear"
              >
                ×
              </button>
            </div>
          )}

          <div style={{ display: 'flex', gap: 2, marginBottom: 8, flexWrap: 'wrap' }}>
            {tabs
              .filter((t) => t.show)
              .map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => {
                    setActiveTab(tab.id);
                    setSearchQuery('');
                  }}
                  style={{
                    padding: '4px 8px',
                    fontSize: 10,
                    border: '1px solid #e5e7eb',
                    borderRadius: 4,
                    background: activeTab === tab.id ? '#3b82f6' : 'white',
                    color: activeTab === tab.id ? 'white' : '#374151',
                    cursor: 'pointer',
                  }}
                >
                  {tab.label}
                </button>
              ))}
          </div>

          {(activeTab === 'equipment' || activeTab === 'external' || activeTab === 'node' || activeTab === 'instrument') && (
            <div style={{ marginBottom: 8 }}>
              <input
                ref={searchRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                placeholder={`Search ${activeTab === 'node' ? 'nodes' : activeTab}...`}
                style={{
                  width: '100%',
                  padding: '6px 10px',
                  border: '1px solid #e5e7eb',
                  borderRadius: 5,
                  fontSize: 11,
                  outline: 'none',
                }}
              />
            </div>
          )}

          <div
            style={{
              border: '1px solid #e5e7eb',
              borderRadius: 6,
              maxHeight: 150,
              overflowY: 'auto',
              background: '#fafafa',
            }}
          >
            {activeTab === 'equipment' && (
              <div style={{ padding: 8 }}>
                {filteredEquipment.length ? (
                  filteredEquipment.map((eq) => {
                    const eqEntity = equipments.find((e) => e.equipment_no === eq.tag);
                    return (
                    <div
                      key={eq.tag}
                      onClick={() => handleSelect(eq.tag, 'equipment')}
                      onMouseEnter={() => eqEntity?.id && onHighlightEntity?.(eqEntity.id)}
                      onMouseLeave={() => onHighlightEntity?.(null)}
                      style={{
                        padding: '6px 8px',
                        cursor: 'pointer',
                        borderRadius: 4,
                        marginBottom: 2,
                        background: value === eq.tag ? '#dbeafe' : 'transparent',
                        fontSize: 11,
                      }}
                    >
                      <div style={{ fontWeight: 500 }}>{eq.tag}</div>
                      <div style={{ color: '#6b7280', fontSize: 10 }}>
                        {eq.name} ({eq.type})
                      </div>
                    </div>
                    );
                  })
                ) : (
                  <div style={{ color: '#9ca3af', fontSize: 11, padding: 8 }}>
                    {searchQuery ? `No equipment matching "${searchQuery}"` : 'No equipment available'}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'external' && (
              <div style={{ padding: 8 }}>
                {filteredExternalRefs.length ? (
                  filteredExternalRefs.map((ref, idx) => (
                    <div
                      key={`${ref.refText}-${ref.pnid || ''}-${ref.lineNo || ref.tag}-${idx}`}
                      onClick={() => handleExternalSelect(ref)}
                      style={{
                        padding: '6px 8px',
                        cursor: 'pointer',
                        borderRadius: 4,
                        marginBottom: 2,
                        background: value === ref.refText || value === ref.tag ? '#dbeafe' : 'transparent',
                        fontSize: 11,
                      }}
                    >
                      <div style={{ fontWeight: 500 }}>{ref.refText}</div>
                      <div style={{ color: '#6b7280', fontSize: 10 }}>
                        {ref.context}
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={{ color: '#9ca3af', fontSize: 11, padding: 8 }}>
                    {searchQuery ? `No external refs matching "${searchQuery}"` : 'No external refs in AI data'}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'node' && (
              <div style={{ padding: 8 }}>
                {filteredNodes.length ? (
                  filteredNodes.map((node) => (
                    <div
                      key={node.id}
                      onClick={() => handleSelect(`Node #${node.node_no}`, 'node')}
                      onMouseEnter={() => onHighlightEntity?.(node.id)}
                      onMouseLeave={() => onHighlightEntity?.(null)}
                      style={{
                        padding: '6px 8px',
                        cursor: 'pointer',
                        borderRadius: 4,
                        marginBottom: 2,
                        background: value === `Node #${node.node_no}` ? '#dbeafe' : 'transparent',
                        fontSize: 11,
                      }}
                    >
                      <div style={{ fontWeight: 500 }}>Node #{node.node_no}</div>
                      <div style={{ color: '#6b7280', fontSize: 10 }}>
                        {node.line_no} ({node.source_ref} → {node.dest_ref})
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={{ color: '#9ca3af', fontSize: 11, padding: 8 }}>
                    {searchQuery ? `No nodes matching "${searchQuery}"` : 'No other nodes available'}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'instrument' && (
              <div style={{ padding: 8 }}>
                {filteredInstruments.length ? (
                  filteredInstruments.map((inst) => {
                    const ref = buildInstrumentRef(inst);
                    return (
                      <div
                        key={inst.id}
                        onClick={() => handleSelect(ref, 'instrument')}
                        onMouseEnter={() => onHighlightEntity?.(inst.id)}
                        onMouseLeave={() => onHighlightEntity?.(null)}
                        style={{
                          padding: '6px 8px',
                          cursor: 'pointer',
                          borderRadius: 4,
                          marginBottom: 2,
                          background: value === ref ? '#dbeafe' : 'transparent',
                          fontSize: 11,
                        }}
                    >
                        <div style={{ fontWeight: 500 }}>{getInstrumentPrimaryLabel(inst)}</div>
                        <div style={{ color: '#6b7280', fontSize: 10 }}>
                          {getInstrumentSecondaryLabel(inst)
                            ? `${getInstrumentSecondaryLabel(inst)} · ${inst.instrument_type || 'Instrument'}`
                            : (inst.instrument_type || 'Instrument')}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div style={{ color: '#9ca3af', fontSize: 11, padding: 8 }}>
                    {searchQuery ? `No instruments matching "${searchQuery}"` : 'No instruments available'}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'drain' && includeDrain && (
              <div style={{ padding: 8 }}>
                <div style={{ fontSize: 10, color: '#6b7280', marginBottom: 6 }}>Select drain/sewer destination</div>
                {Object.entries(DRAIN_TYPES).map(([code, name]) => (
                  <div
                    key={code}
                    onClick={() => handleSelect(code, 'drain')}
                    style={{
                      padding: '6px 8px',
                      cursor: 'pointer',
                      borderRadius: 4,
                      marginBottom: 2,
                      background: value === code ? '#fef3c7' : 'transparent',
                      fontSize: 11,
                    }}
                  >
                    <div style={{ fontWeight: 500 }}>{code}</div>
                    <div style={{ color: '#6b7280', fontSize: 10 }}>{name}</div>
                  </div>
                ))}
              </div>
            )}

            {activeTab === 'manual' && (
              <div style={{ padding: 8 }}>
                <div style={{ fontSize: 10, color: '#6b7280', marginBottom: 6 }}>
                  Enter custom value (for external refs, headers, etc.)
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                  <input
                    type="text"
                    placeholder="e.g., FROM N2 HEADER, TO FLARE"
                    value={manualValue}
                    onChange={(e) => setManualValue(e.target.value)}
                    style={{
                      flex: 1,
                      padding: 6,
                      fontSize: 11,
                      border: `1px solid ${isNodeRefPattern(manualValue) ? '#f59e0b' : '#e5e7eb'}`,
                      borderRadius: 4,
                    }}
                    onKeyDown={(e) => {
                      if (e.key !== 'Enter' && e.key !== 'Tab') return;
                      if (e.key === 'Tab' && e.shiftKey) return;
                      e.preventDefault();
                      if (!manualValue) {
                        if (value.trim()) onAdvance?.();
                        return;
                      }
                      if (isNodeRefPattern(manualValue)) return;
                      handleSelect(manualValue, 'manual');
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => manualValue && !isNodeRefPattern(manualValue) && handleSelect(manualValue, 'manual')}
                    disabled={isNodeRefPattern(manualValue)}
                    style={{
                      padding: '6px 12px',
                      fontSize: 11,
                      background: isNodeRefPattern(manualValue) ? '#d1d5db' : '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: 4,
                      cursor: isNodeRefPattern(manualValue) ? 'not-allowed' : 'pointer',
                    }}
                  >
                    Set
                  </button>
                </div>
                {isNodeRefPattern(manualValue) && (
                  <div
                    style={{
                      fontSize: 10,
                      color: '#dc2626',
                      marginTop: 6,
                      padding: '6px 8px',
                      background: '#fef2f2',
                      borderRadius: 4,
                      border: '1px solid #fecaca',
                    }}
                  >
                    To reference a node, use the "Node/Line" tab. The target node must exist first.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SourceDestSelector;
